// @flow

// https://flow.org/en/docs/react/types/#toc-react-componenttype, React.StatelessFunctionalComponent<Props> or Class<React.Component<Props, any>>
import type { ComponentType } from 'react';
import type { RouterProps } from 'type/type';
import { matchPath, withRouter } from 'react-router';

import * as React from 'react';
import curry from 'lodash/curry';
import some from 'lodash/some';
import compose from 'lodash/fp/compose';
import omit from 'lodash/fp/omit';
import assign from 'lodash/fp/assign';
import prop from 'lodash/fp/prop';

import { PORTAL_ROUTE } from 'services/router/enum';
import { parse } from 'query-string';

type RouterRenderProps = RouterProps & {
  renderInclude: ?Array<string>,
  renderExclude: ?Array<string>
}

export type Routable = {
  transitionTo: (string) => void,
  isRouterPath: (path: string, hasPortalPath: boolean) => boolean,
  query: { [key: string]: string }
}

const routerPropList = [ 'location', 'history', 'match' ];

const appendPortalRoute = (path: string, hasPortalPath: boolean) => {
  if (hasPortalPath) {
    return `${PORTAL_ROUTE}${path}`;
  }

  return path;
};

const getRouterPath = ({
  location: { pathname },
  match: { path, url },
}: RouterProps): string => pathname.replace(url, path);

const withPathMatch = (path: string) => (pattern: { [string]: * }) => !!matchPath(path, pattern);

const omitRouterProps = omit(routerPropList);
const assignTransitionTo = (props: RouterProps) => assign({ transitionTo: props.history.push })(props);
const assignIsRouterPath = (props: RouterProps) => assign({
  isRouterPath: (
    path: string, hasPortalPath: boolean,
  ) => getRouterPath(props) === appendPortalRoute(path, hasPortalPath),
})(props);

const shouldNotRender = (props: RouterRenderProps) => {
  const { renderInclude, renderExclude } = props;
  const path = getRouterPath(props);
  const match = withPathMatch(path);

  return (Array.isArray(renderExclude) && some(renderExclude, pattern => match({ path: pattern })))
    || (Array.isArray(renderInclude) && !some(renderInclude, pattern => match({ path: pattern })));
};

const withQuery = (props: RouterProps) => ({
  ...props,
  query: compose(
    parse,
    prop('location.search'),
  )(props),
});

const routableHoc = curry((WrappedComponent: ComponentType<{}>, props: RouterRenderProps) => {
  return (shouldNotRender(props)
    ? null
    : <WrappedComponent
      {...compose(
        omitRouterProps,
        assignTransitionTo,
        assignIsRouterPath,
        withQuery,
      )(props)} />
  );
},
);

export default compose(withRouter, routableHoc);
